import { useEffect, useRef, useState } from "react";
import { Mic, X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CONVERSATIONS } from "@/constants/testIds";

// Fix N (2026-09-30): grabar una nota de voz desde el panel. Cada navegador
// graba en su formato (ogg, webm o mp4) y el servidor la convierte a la nota
// de voz de WhatsApp.
const PREFERRED_TYPES = ["audio/ogg;codecs=opus", "audio/webm;codecs=opus", "audio/mp4", "audio/webm"];

function pickMimeType() {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return "";
  return PREFERRED_TYPES.find((t) => MediaRecorder.isTypeSupported(t)) || "";
}

function formatSeconds(total) {
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

export default function VoiceRecorder({ onRecorded, onError, disabled }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const cancelledRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => () => {
    clearInterval(timerRef.current);
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      cancelledRef.current = true;
      recorder.stop();
    }
  }, []);

  const start = async () => {
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
    } catch {
      onError?.("No se pudo usar el microfono. Revisa los permisos del navegador.");
      return;
    }
    const mimeType = pickMimeType();
    const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    chunksRef.current = [];
    cancelledRef.current = false;
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      clearInterval(timerRef.current);
      setRecording(false);
      if (cancelledRef.current || !chunksRef.current.length) return;
      const type = recorder.mimeType || mimeType || "audio/webm";
      const ext = type.includes("ogg") ? "ogg" : type.includes("mp4") ? "m4a" : "webm";
      onRecorded(new File([new Blob(chunksRef.current, { type })], `nota-de-voz.${ext}`, { type }));
    };
    recorderRef.current = recorder;
    recorder.start();
    setSeconds(0);
    setRecording(true);
    timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
  };

  const stop = (cancel) => {
    cancelledRef.current = cancel;
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
  };

  if (!recording) {
    return (
      <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={start} disabled={disabled} title="Grabar nota de voz" data-testid={CONVERSATIONS.voiceRecordButton}>
        <Mic className="w-4 h-4" />
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-1 shrink-0" data-testid={CONVERSATIONS.voiceRecordingBar}>
      <span className="flex items-center gap-1.5 text-sm text-destructive px-1">
        <span className="w-2 h-2 rounded-full bg-destructive animate-pulse" />
        {formatSeconds(seconds)}
      </span>
      <Button type="button" variant="ghost" size="icon" onClick={() => stop(true)} title="Descartar" data-testid={CONVERSATIONS.voiceCancelButton}>
        <X className="w-4 h-4" />
      </Button>
      <Button type="button" size="icon" className="bg-brand hover:bg-brand-hover text-white" onClick={() => stop(false)} title="Enviar nota de voz" data-testid={CONVERSATIONS.voiceSendButton}>
        <Send className="w-4 h-4" />
      </Button>
    </div>
  );
}
